// src/pages/FeedbackManagement.js
// 顧客回饋管理頁面

import React, { useState, useEffect } from 'react';

export default function FeedbackManagement() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteValue, setNoteValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  // 載入回饋列表
  const loadFeedbacks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/sglrt/feedback/list?page=${page}&limit=${limit}`);
      const data = await res.json();
      if (data.ok) {
        setFeedbacks(data.rows);
        setTotal(data.total);
      } else {
        alert('載入回饋失敗: ' + data.message);
      }
    } catch (err) {
      console.error(err);
      alert('載入回饋失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedbacks();
  }, [page]);

  // 開始編輯備註
  const startEditNote = (feedback) => {
    setEditingNote(feedback.FEEDBACK_ID);
    setNoteValue(feedback.MY_NOTE || '');
  };

  // 取消編輯
  const cancelEdit = () => {
    setEditingNote(null);
    setNoteValue('');
  };

  // 儲存備註
  const saveNote = async (feedbackId) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/sglrt/feedback/${feedbackId}/note`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: noteValue })
      });

      const data = await res.json();
      if (data.ok) {
        // 更新本地狀態
        setFeedbacks(feedbacks.map(f => 
          f.FEEDBACK_ID === feedbackId 
            ? { ...f, MY_NOTE: noteValue }
            : f
        ));
        setEditingNote(null);
        setNoteValue('');
        alert('備註已儲存');
      } else {
        alert('儲存失敗: ' + data.message);
      }
    } catch (err) {
      console.error(err);
      alert('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      height: '100%',
      overflow: 'auto',
      boxSizing: 'border-box'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#333'
    },
    refreshBtn: {
      padding: '8px 16px',
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    table: {
      width: '100%',
      minWidth: 720,
      borderCollapse: 'collapse',
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    th: {
      padding: '12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      backgroundColor: '#f8f9fa',
      fontWeight: '600',
      fontSize: '16px'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
      fontSize: '16px'
    },
    contentCell: {
      maxWidth: '400px',
      wordBreak: 'break-word',
      lineHeight: '1.5',
      verticalAlign: 'top',
      paddingTop: '12px'
    },
    noteCell: {
      minWidth: '250px',
      verticalAlign: 'top',
      paddingTop: '12px'
    },
    noteTextarea: {
      width: '100%',
      minHeight: '80px',
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      fontFamily: 'inherit',
      resize: 'vertical',
      lineHeight: '1.5'
    },
    noteDisplay: {
      padding: '8px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      minHeight: '32px',
      wordBreak: 'break-word'
    },
    buttonGroup: {
      display: 'flex',
      gap: '8px'
    },
    btn: {
      padding: '4px 12px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px'
    },
    btnPrimary: {
      backgroundColor: '#007bff',
      color: '#fff'
    },
    btnSecondary: {
      backgroundColor: '#6c757d',
      color: '#fff'
    },
    btnSuccess: {
      backgroundColor: '#28a745',
      color: '#fff'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '12px',
      marginTop: '20px'
    },
    pageBtn: {
      padding: '6px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      backgroundColor: '#fff',
      cursor: 'pointer',
      fontSize: '14px'
    },
    pageBtnActive: {
      backgroundColor: '#007bff',
      color: '#fff',
      borderColor: '#007bff'
    },
    emptyNote: {
      color: '#999',
      fontStyle: 'italic'
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>顧客回饋管理</h2>
        <button
          onClick={loadFeedbacks}
          disabled={loading}
          style={styles.refreshBtn}
        >
          {loading ? '載入中...' : '重新整理'}
        </button>
      </div>

      {loading && feedbacks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          載入中...
        </div>
      ) : feedbacks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          尚無回饋記錄
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>回饋內容</th>
                <th style={styles.th}>建立時間</th>
                <th style={styles.th}>管理員備註</th>
                <th style={styles.th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {feedbacks.map(feedback => (
                <tr key={feedback.FEEDBACK_ID}>
                  <td style={styles.td}>{feedback.FEEDBACK_ID}</td>
                  <td style={{ ...styles.td, ...styles.contentCell }}>
                    {feedback.CONTENT}
                  </td>
                  <td style={styles.td}>
                    {new Date(feedback.CREATED_TIME).toLocaleString('zh-TW')}
                  </td>
                  <td style={{ ...styles.td, ...styles.noteCell }}>
                    {editingNote === feedback.FEEDBACK_ID ? (
                      <textarea
                        value={noteValue}
                        onChange={(e) => setNoteValue(e.target.value)}
                        placeholder="輸入備註（可分行）..."
                        style={{
                          ...styles.noteTextarea,
                          minHeight: `${Math.max(80, Math.ceil((feedback.CONTENT?.length || 0) / 40) * 20)}px`
                        }}
                      />
                    ) : (
                      <div style={{
                        ...styles.noteDisplay,
                        minHeight: `${Math.max(40, Math.ceil((feedback.CONTENT?.length || 0) / 40) * 20)}px`
                      }}>
                        {feedback.MY_NOTE ? (
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {feedback.MY_NOTE}
                          </div>
                        ) : (
                          <span style={styles.emptyNote}>（無備註）</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    {editingNote === feedback.FEEDBACK_ID ? (
                      <div style={styles.buttonGroup}>
                        <button
                          onClick={() => saveNote(feedback.FEEDBACK_ID)}
                          disabled={saving}
                          style={{ ...styles.btn, ...styles.btnSuccess }}
                        >
                          {saving ? '儲存中...' : '儲存'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          style={{ ...styles.btn, ...styles.btnSecondary }}
                        >
                          取消
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditNote(feedback)}
                        style={{ ...styles.btn, ...styles.btnPrimary }}
                      >
                        編輯備註
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={styles.pageBtn}
              >
                上一頁
              </button>
              <span>
                第 {page} / {totalPages} 頁（共 {total} 筆）
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={styles.pageBtn}
              >
                下一頁
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

